// Mutation hook for PATCH /api/v1/admin/feed/editorial/{id} — edit an
// existing editorial in place (same field set as create; the card keeps
// its feed position). Invalidates the cockpit list + detail caches and
// the SPA ["feed"] cache so the edit shows on the next feed refresh.

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import {
  ADMIN_EDITORIAL_QUERY_KEY,
  type CreateEditorialPostError,
  type CreateEditorialPostVariables,
} from "./useCreateEditorialPost";
import type { EditorialDetail } from "./useEditorialDetail";

export interface UpdateEditorialPostVariables
  extends CreateEditorialPostVariables {
  id: string;
}

async function patchEditorial(
  variables: UpdateEditorialPostVariables,
): Promise<EditorialDetail> {
  const { id, ...body } = variables;
  const response = await fetch(
    `/api/v1/admin/feed/editorial/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CreateEditorialPostError = {
      code: "EDITORIAL_UPDATE_FAILED",
      message: `Editorial update failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const raw = (await response.json()) as { data: EditorialDetail };
  return raw.data;
}

export function useUpdateEditorialPost(): UseMutationResult<
  EditorialDetail,
  CreateEditorialPostError,
  UpdateEditorialPostVariables
> {
  const queryClient = useQueryClient();
  return useMutation<
    EditorialDetail,
    CreateEditorialPostError,
    UpdateEditorialPostVariables
  >({
    mutationFn: patchEditorial,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ADMIN_EDITORIAL_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ["feed"] });
    },
  });
}
